import { useDebounceValue } from '@sravni/react-utils';
import { useState, useEffect, useMemo, useRef } from 'react';

const DA_DATA_DEBOUNCE = 700;

type CacheEntry<T> = {
  data: Array<Dictionaries.TSelectOption<string, T>>;
  timeoutId: ReturnType<typeof setTimeout>;
};

export function useFetchSuggestions<T>(
  getSuggestions: (getSearchValue: string) => Promise<Array<Dictionaries.TSelectOption<string, T>>>,
  defaultValue = '',
  enableCache = false,
  cacheTTL = 120_000, // TTL in ms, default 2 minutes
): {
  currentSuggestions: Array<Dictionaries.TSelectOption<string, T>>;
  setSearchValue: (value: string) => void;
  isLoading: boolean;
  searchValue: string;
} {
  const [searchValue, setSearchValue] = useState<string>(defaultValue);
  const [currentSuggestions, setCurrentSuggestions] = useState<Array<Dictionaries.TSelectOption<string, T>>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<Map<string, CacheEntry<T>>>(new Map());
  const debounceSearchValue = useDebounceValue<string | undefined>(searchValue, DA_DATA_DEBOUNCE);

  const search = useMemo(
    () => async (value: string) => {
      setIsLoading(true);

      try {
        if (!value) {
          setCurrentSuggestions([]);
          return;
        }

        if (enableCache && cacheRef.current.has(value)) {
          setCurrentSuggestions(cacheRef.current.get(value)!.data);
          return;
        }

        const suggestions = await getSuggestions(value);
        if (enableCache) {
          const existing = cacheRef.current.get(value);
          if (existing?.timeoutId) {
            clearTimeout(existing.timeoutId);
          }

          const timeoutId = setTimeout(() => {
            cacheRef.current.delete(value);
          }, cacheTTL);

          cacheRef.current.set(value, { data: suggestions, timeoutId });
        }

        setCurrentSuggestions(suggestions);
      } catch (e) {
        setCurrentSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [getSuggestions, enableCache, cacheTTL],
  );

  useEffect(() => {
    search(debounceSearchValue || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchValue]);

  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      cache.forEach((entry) => {
        clearTimeout(entry.timeoutId);
      });
      cache.clear();
    };
  }, []);

  return {
    isLoading,
    currentSuggestions,
    searchValue,
    setSearchValue,
  };
}
